import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Plus, Download, Settings2, Tag, Eye, EyeOff, HardDrive, CheckCircle2, XCircle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_TYPES = ["CV", "Appointment Letter", "Joining Form", "Qualification Certificate",
  "Experience Certificate", "ID Proof", "Address Proof", "PAN", "Aadhaar Reference", "Bank Proof",
  "Photo", "PF/UAN Document", "ESIC Document", "Police Verification", "Medical Fitness",
  "Vaccination Certificate", "Training Certificate", "Competency Record", "Authorization Record", "Other"];
const DEFAULT_REQUIRED = new Set(["ID Proof", "Address Proof", "PAN", "Aadhaar Reference", "Bank Proof", "Photo"]);

export default function Documents() {
  const { isSuperAdmin } = useAuth();
  const qc = useQueryClient();
  const [typesOpen, setTypesOpen] = useState(false);
  const [newType, setNewType] = useState("");
  const [hasExpiry, setHasExpiry] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [newTypeDesigs, setNewTypeDesigs] = useState([]);
  const [newTypeMaxSize, setNewTypeMaxSize] = useState(""); // Super Admin only; blank = server default (15 MB)
  const [scopeFor, setScopeFor] = useState(null); // the document type currently being scoped to designations
  const [scopeSelected, setScopeSelected] = useState([]);
  const [sizeFor, setSizeFor] = useState(null); // the document type currently having its max upload size edited (Super Admin only)
  const [sizeValue, setSizeValue] = useState("");

  const { data: docs, isLoading } = useQuery({ queryKey: ["allDocs"], queryFn: async () => (await api.get("/documents")).data });
  const { data: types } = useQuery({ queryKey: ["docTypes"], queryFn: async () => (await api.get("/document-types")).data });
  const { data: desigs } = useQuery({ queryKey: ["designations"], queryFn: async () => (await api.get("/designations")).data });

  const addType = useMutation({
    mutationFn: async (name) => (await api.post("/document-types", {
      name, has_expiry: hasExpiry, required: isRequired, required_for_designations: newTypeDesigs,
      max_size_mb: newTypeMaxSize ? Number(newTypeMaxSize) : null,
      category: "General", status: "Active",
    })).data,
    onSuccess: () => { toast.success("Document type added"); qc.invalidateQueries({ queryKey: ["docTypes"] }); setNewType(""); setHasExpiry(false); setIsRequired(false); setNewTypeDesigs([]); setNewTypeMaxSize(""); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const toggleRequired = useMutation({
    mutationFn: async (t) => (await api.patch(`/document-types/${t.id}`, { name: t.name, category: t.category, has_expiry: t.has_expiry, required: !t.required, required_for_designations: t.required_for_designations || [], max_size_mb: t.max_size_mb ?? null, status: t.status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["docTypes"] }),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const saveSize = useMutation({
    mutationFn: async () => (await api.patch(`/document-types/${sizeFor.id}`, {
      name: sizeFor.name, category: sizeFor.category, has_expiry: sizeFor.has_expiry,
      required: sizeFor.required, required_for_designations: sizeFor.required_for_designations || [],
      max_size_mb: sizeValue ? Number(sizeValue) : null, status: sizeFor.status,
    })).data,
    onSuccess: () => { toast.success("Max upload size updated"); qc.invalidateQueries({ queryKey: ["docTypes"] }); setSizeFor(null); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  // "Inactive" types are excluded entirely from every employee's My Documents
  // checklist (GET /api/my/documents filters status==Active server-side) —
  // this is how a type like ESIC/ID Proof/Joining Form/PF-UAN gets fully
  // removed from showing as "Pending" without losing the historical record
  // or any documents already uploaded under that type. They still appear
  // here (dimmed) and in the per-employee upload dropdown, so HR can
  // reactivate or still file an old-format document against them.
  const toggleActive = useMutation({
    mutationFn: async (t) => (await api.patch(`/document-types/${t.id}`, { name: t.name, category: t.category, has_expiry: t.has_expiry, required: t.required, required_for_designations: t.required_for_designations || [], max_size_mb: t.max_size_mb ?? null, status: t.status === "Active" ? "Inactive" : "Active" })).data,
    onSuccess: (_, t) => { toast.success(t.status === "Active" ? "Hidden from employee checklists" : "Restored to employee checklists"); qc.invalidateQueries({ queryKey: ["docTypes"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const saveScope = useMutation({
    mutationFn: async () => (await api.patch(`/document-types/${scopeFor.id}`, {
      name: scopeFor.name, category: scopeFor.category, has_expiry: scopeFor.has_expiry,
      required: scopeFor.required, required_for_designations: scopeSelected,
      max_size_mb: scopeFor.max_size_mb ?? null, status: scopeFor.status,
    })).data,
    onSuccess: () => { toast.success("Updated which designations this applies to"); qc.invalidateQueries({ queryKey: ["docTypes"] }); setScopeFor(null); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const toggleDesig = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const desigName = (id) => (desigs || []).find((d) => d.id === id)?.title || id;

  const seedDefaults = async () => {
    const existing = new Set((types || []).map((t) => t.name));
    const missing = DEFAULT_TYPES.filter((t) => !existing.has(t));
    for (const t of missing) { try { await api.post("/document-types", { name: t, has_expiry: ["Police Verification","Medical Fitness","Vaccination Certificate","Training Certificate","Competency Record","Authorization Record"].includes(t), required: DEFAULT_REQUIRED.has(t), category: "General", status: "Active" }); } catch {} }
    qc.invalidateQueries({ queryKey: ["docTypes"] });
    toast.success(`Seeded ${missing.length} standard document types`);
  };

  const download = async (id, name) => {
    try { const res = await api.get(`/documents/${id}/download`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); }
    catch { toast.error("Unable to download"); }
  };

  // Employee self-uploads are staged locally (never Google Drive — the whole
  // company would otherwise need access to the shared intencivhealthcare@
  // Drive account) until HR/Super Admin verifies them here or from the
  // employee's own record. Approve pushes the file to Drive; reject discards
  // it and lets the employee try again (they're notified in-app either way).
  const verifyDoc = useMutation({
    mutationFn: async ({ docId, action, remarks }) => (await api.post(`/documents/${docId}/verify`, { action, remarks })).data,
    onSuccess: (res) => { toast.success(res.status === "Active" ? "Document verified and filed" : "Document rejected — employee notified"); qc.invalidateQueries({ queryKey: ["allDocs"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const approveDoc = (d) => {
    if (window.confirm(`Verify and file "${d.document_type}" for ${d.employee_name}? This uploads it to Google Drive and marks it Active.`)) {
      verifyDoc.mutate({ docId: d.id, action: "approve" });
    }
  };

  const rejectDoc = (d) => {
    const remarks = window.prompt(`Reject "${d.document_type}" for ${d.employee_name}? Optionally add a note for the employee (shown in their notification), or leave blank.`, "");
    if (remarks !== null) {
      verifyDoc.mutate({ docId: d.id, action: "reject", remarks: remarks || "" });
    }
  };

  const pendingCount = (docs || []).filter((d) => d.status === "PendingVerification").length;
  const [pendingOnly, setPendingOnly] = useState(false);
  const visibleDocs = pendingOnly ? (docs || []).filter((d) => d.status === "PendingVerification") : (docs || []);

  return (
    <div>
      <PageHeader overline="Personnel Documents" title="Document Management" icon={FileText}
        subtitle="Every document tracked with version, expiry and audit. Employee self-uploads are staged locally and only reach Google Drive after HR/Super Admin verification here."
        actions={<>
          {pendingCount > 0 && (
            <Button variant={pendingOnly ? "default" : "outline"} className={pendingOnly ? "bg-amber-500 hover:bg-amber-600" : "border-amber-300 text-amber-700"}
              onClick={() => setPendingOnly((v) => !v)} data-testid="pending-filter-btn">
              <ShieldAlert className="h-4 w-4 mr-1" /> {pendingCount} Awaiting Verification
            </Button>
          )}
          <Button variant="outline" onClick={() => setTypesOpen(true)} data-testid="manage-types-btn"><Settings2 className="h-4 w-4 mr-1" /> Manage Types</Button>
        </>}
      />
      {isLoading ? <Loading /> : (docs || []).length === 0 ? (
        <EmptyState icon={FileText} title="No documents uploaded yet" hint="Open an employee record to upload documents. First, configure document types."
          action={<Button className="bg-navy" onClick={() => setTypesOpen(true)}><Settings2 className="h-4 w-4 mr-1" /> Configure Types</Button>} />
      ) : visibleDocs.length === 0 ? (
        <EmptyState icon={ShieldAlert} title="Nothing awaiting verification" hint="Every submitted document has been reviewed." action={<Button variant="outline" onClick={() => setPendingOnly(false)}>Show All Documents</Button>} />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Employee</TableHead>
              <TableHead className="overline text-slate-500">Type</TableHead>
              <TableHead className="overline text-slate-500">Ver</TableHead>
              <TableHead className="overline text-slate-500">Expiry</TableHead>
              <TableHead className="overline text-slate-500">Status</TableHead>
              <TableHead className="text-right overline text-slate-500">File</TableHead>
              <TableHead className="text-right overline text-slate-500">Verify</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {visibleDocs.map((d) => (
                <TableRow key={d.id} className="table-row-i" data-testid={`gdoc-row-${d.id}`}>
                  <TableCell className="font-medium text-navy">{d.employee_name}<span className="text-slate-400 text-xs font-mono-i ml-2">{d.employee_code}</span></TableCell>
                  <TableCell className="text-slate-600">{d.document_type}</TableCell>
                  <TableCell className="text-slate-500">v{d.version}</TableCell>
                  <TableCell className="text-slate-600">{d.expiry_date || "—"}</TableCell>
                  <TableCell><StatusBadge status={d.expiry_status === "None" ? d.status : d.expiry_status} /></TableCell>
                  <TableCell className="text-right">
                    {d.status !== "Rejected" && (
                      <Button size="sm" variant="ghost" title={d.status === "PendingVerification" ? "Preview before verifying" : undefined}
                        onClick={() => download(d.id, d.file_name)} data-testid={`download-${d.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {d.status === "PendingVerification" ? (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" className="text-emerald-700" title="Verify & file to Google Drive"
                          onClick={() => approveDoc(d)} data-testid={`approve-${d.id}`}>
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" title="Reject — employee will be asked to submit again"
                          onClick={() => rejectDoc(d)} data-testid={`reject-${d.id}`}>
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={typesOpen} onOpenChange={setTypesOpen}>
        <DialogContent className="bg-white" data-testid="types-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Document Types</DialogTitle></DialogHeader>
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Configure the master list of document types.</p>
            <Button size="sm" variant="outline" onClick={seedDefaults} data-testid="seed-types">Load Standard Set</Button>
          </div>
          <p className="text-xs text-slate-400 -mt-1">Click a type's name to toggle whether it's required for everyone. Click the tag icon to restrict it to specific designations instead (e.g. Registration Certificate for only Technician/Doctor) — a designation-restricted type is hidden from everyone else's checklist. Click the eye icon to hide a type entirely from every employee's checklist (e.g. ESIC Documents, ID Proof, Joining Form, PF/UAN Document) — it's kept here, dimmed, so you can bring it back later.{isSuperAdmin && " Click the drive icon to set the max upload size for this type."}</p>
          <div className="max-h-52 overflow-y-auto flex flex-wrap gap-2 py-2">
            {(types || []).map((t) => {
              const scoped = t.required_for_designations || [];
              const inactive = t.status !== "Active";
              return (
                <div key={t.id} className={`flex items-stretch rounded-full overflow-hidden border ${inactive ? "opacity-40" : ""} ${scoped.length ? "border-teal/40" : t.required ? "border-navy" : "border-transparent"}`}>
                  <button type="button" onClick={() => toggleRequired.mutate(t)}
                    className={`text-xs px-3 py-1 ${t.required ? "bg-navy text-white" : "bg-slate-100 text-slate-700"}`}>
                    {t.name}{t.has_expiry && " ⏳"}{t.required && !scoped.length && " •required"}{inactive && " •hidden"}
                  </button>
                  <button type="button" title="Restrict to specific designations"
                    onClick={() => { setScopeFor(t); setScopeSelected(scoped); }}
                    className={`text-[11px] px-2 py-1 border-l flex items-center gap-1 ${scoped.length ? "bg-teal/10 text-teal border-teal/30" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                    {scoped.length ? scoped.map(desigName).join(", ") : <Tag className="h-3 w-3" />}
                  </button>
                  {isSuperAdmin && (
                    <button type="button" title="Set max upload size (Super Admin only)"
                      onClick={() => { setSizeFor(t); setSizeValue(t.max_size_mb ? String(t.max_size_mb) : ""); }}
                      className={`text-[11px] px-2 py-1 border-l flex items-center gap-1 ${t.max_size_mb ? "bg-navy/5 text-navy border-navy/20" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                      {t.max_size_mb ? `${t.max_size_mb} MB` : <HardDrive className="h-3 w-3" />}
                    </button>
                  )}
                  <button type="button" title={inactive ? "Show on employee checklists again" : "Hide from every employee checklist"}
                    onClick={() => toggleActive.mutate(t)}
                    className={`text-[11px] px-2 py-1 border-l flex items-center gap-1 ${inactive ? "bg-amber-50 text-amber-600 border-amber-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                    {inactive ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </button>
                </div>
              );
            })}
            {(types || []).length === 0 && <p className="text-sm text-slate-400">None yet.</p>}
          </div>
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1"><Label>New Type</Label><Input value={newType} onChange={(e) => setNewType(e.target.value)} className="mt-1" data-testid="new-type-name" /></div>
              <div className="flex items-center gap-2 pb-2"><Switch checked={hasExpiry} onCheckedChange={setHasExpiry} data-testid="type-expiry" /><span className="text-sm text-slate-600">Expires</span></div>
              <div className="flex items-center gap-2 pb-2"><Switch checked={isRequired} onCheckedChange={setIsRequired} data-testid="type-required" /><span className="text-sm text-slate-600">Required</span></div>
              {isSuperAdmin && (
                <div className="w-28"><Label className="text-xs text-slate-500">Max MB</Label>
                  <Input type="number" min="1" placeholder="15" value={newTypeMaxSize} onChange={(e) => setNewTypeMaxSize(e.target.value)} className="mt-1" data-testid="new-type-max-size" />
                </div>
              )}
              <Button className="bg-navy hover:bg-navy-700" disabled={!newType || addType.isPending} onClick={() => addType.mutate(newType)} data-testid="add-type"><Plus className="h-4 w-4" /></Button>
            </div>
            {(desigs || []).length > 0 && (
              <div>
                <Label className="text-xs text-slate-500">Or restrict to specific designations instead of "Required" (e.g. Registration Certificate → Technician, Doctor)</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {desigs.map((d) => (
                    <button key={d.id} type="button" onClick={() => toggleDesig(newTypeDesigs, setNewTypeDesigs, d.id)}
                      className={`text-[11px] rounded-full px-2.5 py-1 border ${newTypeDesigs.includes(d.id) ? "bg-teal text-white border-teal" : "bg-slate-100 text-slate-600 border-transparent"}`}>
                      {d.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!scopeFor} onOpenChange={(o) => !o && setScopeFor(null)}>
        <DialogContent className="bg-white" data-testid="scope-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-xl text-navy">Restrict "{scopeFor?.name}" to Designations</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Select one or more designations to make this document required only for employees in those roles, and hidden from everyone else's checklist. Leave none selected to go back to the plain "Required" toggle (applies to everyone, or no one).</p>
          <div className="max-h-64 overflow-y-auto flex flex-wrap gap-2 py-2">
            {(desigs || []).map((d) => (
              <button key={d.id} type="button" onClick={() => toggleDesig(scopeSelected, setScopeSelected, d.id)}
                className={`text-xs rounded-full px-3 py-1 border ${scopeSelected.includes(d.id) ? "bg-teal text-white border-teal" : "bg-slate-100 text-slate-700 border-transparent"}`}>
                {d.title}
              </button>
            ))}
            {(desigs || []).length === 0 && <p className="text-sm text-slate-400">No designations configured yet.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScopeFor(null)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={saveScope.isPending} onClick={() => saveScope.mutate()} data-testid="save-scope">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sizeFor} onOpenChange={(o) => !o && setSizeFor(null)}>
        <DialogContent className="bg-white" data-testid="size-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-xl text-navy">Max Upload Size — "{sizeFor?.name}"</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Applies to both HR uploads and employee self-uploads for this document type. Leave blank to use the server default (15 MB).</p>
          <div><Label>Max Size (MB)</Label>
            <Input type="number" min="1" placeholder="15 (default)" value={sizeValue} onChange={(e) => setSizeValue(e.target.value)} className="mt-1" data-testid="size-value" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSizeFor(null)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={saveSize.isPending} onClick={() => saveSize.mutate()} data-testid="save-size">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
